/**
 * KestrelViewerProvider
 *
 * This provider owns all viewer-specific UI state and exposes it via context.
 * It also provides KestrelViewerBridge, which allows PixelStreamUI to access
 * current values for message handling.
 *
 * Initialization is handled by the KestrelInitializer component, which is
 * rendered as a child of this provider.
 */
import { PixelStreaming } from '@epicgames-ps/lib-pixelstreamingfrontend-ue5.5'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useRolodexEdit } from 'features/fde'
import { useGeckoPermissions } from 'features/permissions'
import React, {
  createContext,
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Vector2 } from 'three'
import {
  KVCameraPose,
  KVDataLayersConfig,
  KVInspectionGrid,
  KVMarkup,
  KVNavigationMode,
  KVPhoto,
  KVPosition,
  KVResolutionQuality,
  KVRotation,
  KVScreenshotResult,
  KVUnitType,
  KVUserSettings,
} from 'types'

import { useAnnotationCounters } from './hooks/useAnnotationCounters'
import { useDataLayerManagement } from './hooks/useDataLayerManagement'
import { useTemperatureProbe } from './hooks/useTemperatureProbe'
import { KestrelInitializer } from './KestrelInitializer'
import { useKestrel } from './KestrelProvider'
import type { KestrelViewerBridge } from './types/kestrelViewerBridge'
import type { KVActiveLayer, KVTemperature } from './types/kestrelViewerTypes'
import { usePhotoFiles } from './ui/tools/photoreel/hooks/usePhotoFiles'
import type { ParsedMessage } from './utils/messageUtils'

/**
 * KestrelViewerContext: Ephemeral UI state for the current viewer session.
 *
 * Use this for:
 * - Tool/mode selection (selectedMode, measurementType)
 * - Panel visibility (settingsOpen, photoReelOpen, helperTextVisible)
 * - Viewer preferences (navigationMode, resolutionQuality, currentUnit)
 * - Data layer/thermal configuration
 * - Photo reel state and filtering
 * - Communication with Unreal (sendCommand)
 *
 * Do NOT use for persisted model data—use KestrelContext instead.
 */
export type KestrelViewerContextType = {
  sendCommand: (json: string | object) => void
  selectedMode: string
  setSelectedMode: Dispatch<SetStateAction<string>>
  selectedMarkup: KVMarkup | undefined
  setSelectedMarkup: Dispatch<SetStateAction<KVMarkup | undefined>>
  popupMenuStartPosition: Vector2
  helperTextVisible: boolean
  setHelperTextVisible: Dispatch<SetStateAction<boolean>>
  settingsOpen: boolean
  setSettingsOpen: Dispatch<SetStateAction<boolean>>
  infoOpen: boolean
  setInfoOpen: Dispatch<SetStateAction<boolean>>
  inspectionGridSettingsOpen: boolean
  setInspectionGridSettingsOpen: Dispatch<SetStateAction<boolean>>
  inspectionGridSettings: KVInspectionGrid | undefined
  setInspectionGridSettings: Dispatch<
    SetStateAction<KVInspectionGrid | undefined>
  >
  settings: KVUserSettings | undefined
  setSettings: Dispatch<SetStateAction<KVUserSettings>>
  is2DPlaneModeActive: boolean
  setIs2DPlaneModeActive: Dispatch<SetStateAction<boolean>>
  planePosition?: KVPosition
  setPlanePosition?: Dispatch<SetStateAction<KVPosition | undefined>>
  planeRotation?: KVRotation
  setPlaneRotation?: Dispatch<SetStateAction<KVRotation | undefined>>
  measurementType: string
  setMeasurementType: Dispatch<SetStateAction<string>>
  photoReelOpen: boolean
  setPhotoReelOpen: Dispatch<SetStateAction<boolean>>
  photoReelDocked: boolean
  setPhotoReelDocked: Dispatch<SetStateAction<boolean>>
  photoReelMode: 'floating' | 'docked'
  setPhotoReelMode: Dispatch<SetStateAction<'floating' | 'docked'>>
  selectedPhoto: KVPhoto | null
  setSelectedPhoto: Dispatch<SetStateAction<KVPhoto | null>>
  photos: KVPhoto[]
  setPhotos: Dispatch<SetStateAction<KVPhoto[]>>
  filteredPhotos: KVPhoto[] | null
  setFilteredPhotos: Dispatch<SetStateAction<KVPhoto[] | null>>
  photoRotations: Record<string, number>
  getPhotoRotation: (photoId: string) => number
  setPhotoRotation: (photoId: string, rotation: number) => void
  savePhotoRotations: () => Promise<void>
  photosLoading: boolean
  photosError: Error | null
  containerRef: RefObject<HTMLDivElement | null>
  navigationMode: KVNavigationMode
  setNavigationMode: Dispatch<SetStateAction<KVNavigationMode>>
  cameraPosesVisible: boolean
  setCameraPosesVisible: Dispatch<SetStateAction<boolean>>
  cameraPoses: KVCameraPose[] | undefined
  filteringPhotos: boolean
  setFilteringPhotos: Dispatch<SetStateAction<boolean>>
  resolutionQuality: KVResolutionQuality
  setResolutionQuality: Dispatch<SetStateAction<KVResolutionQuality>>
  thermalGradientVisible: boolean
  setThermalGradientVisible: Dispatch<SetStateAction<boolean>>
  activeLayer: KVActiveLayer
  setActiveLayer: Dispatch<SetStateAction<KVActiveLayer>>
  availableLayerPrefixes: string[]
  dataLayersConfig: KVDataLayersConfig | undefined
  currentTemperature: KVTemperature | null
  setCurrentTemperature: Dispatch<SetStateAction<KVTemperature | null>>
  temperatureProbeLoading: boolean
  setTemperatureProbeLoading: Dispatch<SetStateAction<boolean>>
  screenshotResult: KVScreenshotResult | null
  setScreenshotResult: Dispatch<SetStateAction<KVScreenshotResult | null>>
  currentUnit: KVUnitType
  setCurrentUnit: Dispatch<SetStateAction<KVUnitType>>
  dataValidationPanelOpen: boolean
  setDataValidationPanelOpen: Dispatch<SetStateAction<boolean>>
  textSegmentationPopoverVisible: boolean
  setTextSegmentationPopoverVisible: Dispatch<SetStateAction<boolean>>
  orientationAxesPopoverVisible: boolean
  setOrientationAxesPopoverVisible: Dispatch<SetStateAction<boolean>>
  /** Buffer of the most recently received messages from Kestrel (last 100) */
  recentMessages: ParsedMessage[]
  /** Set callback to be notified when textSegmentation batch annotation is received */
  onTextSegmentationComplete?: (callback: () => void) => void
  /** Set callback to be notified when orientation forward vector result is received */
  onOrientationForwardVectorResult?: (
    callback: (forwardVector: { x: number; y: number; z: number }) => void
  ) => void
}

// ============================================================================
// Context and Hook
// ============================================================================

export const KestrelViewerContext = createContext<
  KestrelViewerContextType | undefined
>(undefined)

export const useKestrelViewerContext = () => {
  const context = useContext(KestrelViewerContext)
  if (!context)
    throw new Error(
      'useKestrelViewerContext() must be used within KestrelViewerProvider'
    )
  return context
}

// ============================================================================
// Provider Props
// ============================================================================

export interface KestrelViewerProviderProps {
  children: React.ReactNode

  /** The PixelStreaming instance, available after connection setup */
  pixelStreaming: PixelStreaming | undefined

  /** Ref to the video parent element for positioning calculations */
  videoParentRef: RefObject<HTMLDivElement | null>

  /** Ref to container element */
  containerRef: RefObject<HTMLDivElement | null>

  /** Bridge ref that provider will populate with getter functions */
  bridgeRef: RefObject<KestrelViewerBridge>

  /** Mouse position tracker ref from PixelStreamUI */
  mouseTrackerRef: RefObject<Vector2>

  /** Optional whiteboard ID when in SystemView context */
  selectedWhiteboardId?: string | null

  /** Buffer of recent messages for debug tools */
  recentMessages: ParsedMessage[]
}

// ============================================================================
// Provider Component
// ============================================================================

export const KestrelViewerProvider: React.FC<KestrelViewerProviderProps> = ({
  children,
  pixelStreaming,
  videoParentRef,
  containerRef,
  bridgeRef,
  mouseTrackerRef,
  selectedWhiteboardId,
  recentMessages,
}) => {
  const { user = { name: '', email: '' } } = useGeckoPermissions()
  const { authorization } = useGeckoAuth()
  const { rolodexPatch } = useRolodexEdit()
  const { KESTREL_API_URL, ROLODEX_URL } = useRuntimeVariables()

  // Get data from KestrelProvider
  const {
    kestrelModel,
    kestrelModelCameras,
    gatekeeper,
    authorizedAxios,
    selectedMarkup,
    setSelectedMarkup,
    setPopupMenuStartPosition,
    popupMenuStartPosition,
    annotations,
    setAnnotations,
    historicalAnnotations,
    setHistoricalAnnotations,
    measurements,
    setMeasurements,
    planeIntersections,
    setPlaneIntersections,
    cameraViews,
    setCameraViews,
    cameraPoses,
    inspectionAreas,
    setInspectionAreas,
    primitives,
    setPrimitives,
    sendKVCommand,
  } = useKestrel()

  // ============================================================================
  // Data Loading Hooks
  // ============================================================================

  // Load photos for the kestrel model
  const {
    photos: serverPhotos,
    isLoading: photosLoading,
    error: photosError,
  } = usePhotoFiles(
    kestrelModel?.data.photosEnabled ? kestrelModel.id : undefined
  )

  // Counter management for generating unique annotation/measurement labels
  const {
    nextAnnotationNumber,
    setNextAnnotationNumber,
    nextMeasurementNumber,
    setNextMeasurementNumber,
  } = useAnnotationCounters(annotations, measurements, kestrelModel?.id)

  // ============================================================================
  // UI State
  // ============================================================================

  const [selectedMode, setSelectedMode] = useState('cursor')
  const [measurementType, setMeasurementType] = useState('straightLine')
  const [helperTextVisible, setHelperTextVisible] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [infoOpen, setInfoOpen] = useState(false)
  const [inspectionGridSettingsOpen, setInspectionGridSettingsOpen] =
    useState(false)
  const [textSegmentationPopoverVisible, setTextSegmentationPopoverVisible] =
    useState(false)
  const [orientationAxesPopoverVisible, setOrientationAxesPopoverVisible] =
    useState(false)

  // Settings with localStorage persistence
  const [settings, setSettings] = useState<KVUserSettings>(() => {
    try {
      const stored = localStorage.getItem('kestrelViewer_settings')
      if (stored) {
        const parsed = JSON.parse(stored)
        return KVUserSettings.fromJSON(parsed)
      }
    } catch (error) {
      console.warn('Failed to load settings from localStorage:', error)
    }
    return new KVUserSettings()
  })

  useEffect(() => {
    try {
      localStorage.setItem('kestrelViewer_settings', JSON.stringify(settings))
    } catch (error) {
      console.warn('Failed to save settings to localStorage:', error)
    }
  }, [settings])

  const [inspectionGridSettings, setInspectionGridSettings] =
    useState<KVInspectionGrid>()
  const [is2DPlaneModeActive, setIs2DPlaneModeActive] = useState(false)
  const [planePosition, setPlanePosition] = useState<KVPosition>()
  const [planeRotation, setPlaneRotation] = useState<KVRotation>()

  // Photo state
  const [photoReelOpen, setPhotoReelOpen] = useState(false)
  const [photoReelDocked, setPhotoReelDocked] = useState(false)
  const [photoReelMode, setPhotoReelMode] = useState<'floating' | 'docked'>(
    'floating'
  )
  const [cameraPosesVisible, setCameraPosesVisible] = useState(false)
  const [photos, setPhotos] = useState<KVPhoto[]>([])
  const [selectedPhoto, setSelectedPhoto] = useState<KVPhoto | null>(null)
  const [filteredPhotos, setFilteredPhotos] = useState<KVPhoto[] | null>(null)
  const [photoRotations, setPhotoRotations] = useState<Record<string, number>>(
    {}
  )

  // Sync local photos state with server photos
  useEffect(() => {
    setPhotos(serverPhotos)
  }, [serverPhotos])

  // Initialize photo rotations from model
  useEffect(() => {
    setPhotoRotations(kestrelModel?.data.photoRotations ?? {})
  }, [kestrelModel?.data.photoRotations])

  const getPhotoRotation = useCallback(
    (photoId: string) => photoRotations[photoId] ?? 0,
    [photoRotations]
  )

  const setPhotoRotation = useCallback((photoId: string, rotation: number) => {
    setPhotoRotations((prev) => ({
      ...prev,
      [photoId]: rotation,
    }))
  }, [])

  const savePhotoRotations = useCallback(async () => {
    if (!kestrelModel?.id) return

    // Normalize rotations to 0-360 and filter out zero rotations
    const normalizeRotation = (r: number) => r % 360
    const normalizedRotations = Object.fromEntries(
      Object.entries(photoRotations)
        .map(([k, v]) => [k, normalizeRotation(v)])
        .filter(([, v]) => v !== 0)
    )

    // Only save if there are rotations to save or if we need to clear existing ones
    const hasExistingRotations =
      kestrelModel.data.photoRotations &&
      Object.keys(kestrelModel.data.photoRotations).length > 0
    const hasNewRotations = Object.keys(normalizedRotations).length > 0

    if (hasNewRotations || hasExistingRotations) {
      await rolodexPatch([
        {
          id: kestrelModel.id,
          data: { photoRotations: normalizedRotations },
        },
      ])
    }
  }, [kestrelModel, photoRotations, rolodexPatch])

  // Navigation and view state
  const [navigationMode, setNavigationMode] =
    useState<KVNavigationMode>('orbit')
  const [filteringPhotos, setFilteringPhotos] = useState(false)
  const [thermalGradientVisible, setThermalGradientVisible] = useState(false)
  const [dataValidationPanelOpen, setDataValidationPanelOpen] = useState(false)
  const [resolutionQuality, setResolutionQuality] =
    useState<KVResolutionQuality>(KVResolutionQuality.Standard)

  // Screenshot and unit state
  const [screenshotResult, setScreenshotResult] =
    useState<KVScreenshotResult | null>(null)
  const getFallbackUnit = () => gatekeeper.fallbackUnit
  const initialUnit = kestrelModel?.data?.defaultUnit ?? getFallbackUnit()
  const [currentUnit, setCurrentUnit] = useState<KVUnitType>(
    initialUnit as KVUnitType
  )

  // Text segmentation callback ref
  const textSegmentationCompleteCallbackRef = useRef<(() => void) | undefined>(
    undefined
  )

  // Orientation forward vector callback ref
  const orientationForwardVectorCallbackRef = useRef<
    ((forwardVector: { x: number; y: number; z: number }) => void) | undefined
  >(undefined)

  // ============================================================================
  // Custom hooks for data layer and temperature management
  // (These hooks internally manage their own event listeners)
  // ============================================================================

  const {
    activeLayer,
    setActiveLayer,
    availableLayerPrefixes,
    currentTemperature,
    setCurrentTemperature,
  } = useDataLayerManagement({
    kestrelModel,
  })

  const { temperatureProbeLoading, setTemperatureProbeLoading } =
    useTemperatureProbe()

  // ============================================================================
  // Bridge setup - populate bridge ref with message handler getter
  // ============================================================================

  // This is outside of a `useEffect` so that it runs synchronously with the
  // component's render instead of asynchronously after to avoid race
  // conditions.
  if (bridgeRef.current) {
    bridgeRef.current.messageCallbackAPI = {
      pixelStream: pixelStreaming,
      user: { name: user.name ?? '', email: user.email ?? '' },
      videoParent: videoParentRef as RefObject<HTMLDivElement>,
      setSelectedMarkup,
      mouseTrackerRef: mouseTrackerRef,
      setPopupMenuStartPosition: setPopupMenuStartPosition!,
      annotations,
      setAnnotations,
      historicalAnnotations,
      setHistoricalAnnotations,
      inspectionGridSettings,
      setInspectionGridSettings,
      settings,
      setSettings,
      measurements,
      setMeasurements,
      cameraViews,
      setCameraViews,
      navigationMode,
      setNavigationMode,
      rolodexPatch,
      kestrelModel,
      kestrelModelCameras,
      planePosition,
      setPlanePosition,
      planeRotation,
      setPlaneRotation,
      planeIntersections,
      setPlaneIntersections,
      photoReelOpen,
      setPhotoReelOpen,
      photoReelDocked,
      setPhotoReelDocked,
      selectedPhoto,
      setSelectedPhoto,
      photos,
      setPhotos,
      filteredPhotos,
      setFilteredPhotos,
      authorizedAxios,
      kestrelApiUrl: KESTREL_API_URL ?? '',
      rolodexUrl: ROLODEX_URL ?? '',
      authToken: authorization?.split(' ')[1] ?? '',
      filteringPhotos,
      setFilteringPhotos,
      currentTemperature,
      setCurrentTemperature,
      temperatureProbeLoading,
      setTemperatureProbeLoading,
      selectedMode,
      setScreenshotResult,
      nextAnnotationNumber,
      setNextAnnotationNumber,
      nextMeasurementNumber,
      setNextMeasurementNumber,
      inspectionAreas,
      setInspectionAreas,
      primitives,
      setPrimitives,
      resolutionQuality,
      setResolutionQuality,
      selectedWhiteboardId,
      onTextSegmentationComplete: () => {
        textSegmentationCompleteCallbackRef.current?.()
      },
      onOrientationForwardVectorResult: (forwardVector: {
        x: number
        y: number
        z: number
      }) => {
        orientationForwardVectorCallbackRef.current?.(forwardVector)
      },
    }
  }

  // ============================================================================
  // Build sendCommand function
  // ============================================================================

  const sendCommand = useMemo(() => {
    return (json: string | object) => {
      sendKVCommand?.(json)
    }
  }, [sendKVCommand])

  // ============================================================================
  // Context value
  // ============================================================================

  const contextValue: KestrelViewerContextType = useMemo(
    () => ({
      sendCommand,
      recentMessages,
      selectedMode,
      setSelectedMode,
      selectedMarkup,
      setSelectedMarkup,
      popupMenuStartPosition,
      helperTextVisible,
      setHelperTextVisible,
      inspectionGridSettingsOpen,
      setInspectionGridSettingsOpen,
      settingsOpen,
      setSettingsOpen,
      infoOpen,
      setInfoOpen,
      settings,
      setSettings,
      inspectionGridSettings,
      setInspectionGridSettings,
      is2DPlaneModeActive,
      setIs2DPlaneModeActive,
      planePosition,
      setPlanePosition,
      planeRotation,
      setPlaneRotation,
      measurementType,
      setMeasurementType,
      photoReelOpen,
      setPhotoReelOpen,
      photoReelDocked,
      setPhotoReelDocked,
      photoReelMode,
      setPhotoReelMode,
      selectedPhoto,
      setSelectedPhoto,
      photos,
      setPhotos,
      filteredPhotos,
      setFilteredPhotos,
      photoRotations,
      getPhotoRotation,
      setPhotoRotation,
      savePhotoRotations,
      photosLoading,
      photosError,
      containerRef,
      navigationMode,
      setNavigationMode,
      cameraPosesVisible,
      setCameraPosesVisible,
      cameraPoses,
      filteringPhotos,
      setFilteringPhotos,
      resolutionQuality,
      setResolutionQuality,
      thermalGradientVisible,
      setThermalGradientVisible,
      activeLayer,
      setActiveLayer,
      availableLayerPrefixes,
      dataLayersConfig: kestrelModel?.data.dataLayersConfig,
      currentTemperature,
      setCurrentTemperature,
      temperatureProbeLoading,
      setTemperatureProbeLoading,
      screenshotResult,
      setScreenshotResult,
      currentUnit,
      setCurrentUnit,
      dataValidationPanelOpen,
      setDataValidationPanelOpen,
      textSegmentationPopoverVisible,
      setTextSegmentationPopoverVisible,
      orientationAxesPopoverVisible,
      setOrientationAxesPopoverVisible,
      onTextSegmentationComplete: (callback: () => void) => {
        textSegmentationCompleteCallbackRef.current = callback
      },
      onOrientationForwardVectorResult: (
        callback: (forwardVector: { x: number; y: number; z: number }) => void
      ) => {
        orientationForwardVectorCallbackRef.current = callback
      },
    }),
    [
      sendCommand,
      recentMessages,
      selectedMode,
      selectedMarkup,
      setSelectedMarkup,
      popupMenuStartPosition,
      helperTextVisible,
      inspectionGridSettingsOpen,
      settingsOpen,
      infoOpen,
      settings,
      inspectionGridSettings,
      is2DPlaneModeActive,
      planePosition,
      planeRotation,
      measurementType,
      photoReelOpen,
      photoReelDocked,
      photoReelMode,
      selectedPhoto,
      photos,
      filteredPhotos,
      photoRotations,
      getPhotoRotation,
      setPhotoRotation,
      savePhotoRotations,
      photosLoading,
      photosError,
      containerRef,
      navigationMode,
      cameraPosesVisible,
      cameraPoses,
      filteringPhotos,
      resolutionQuality,
      thermalGradientVisible,
      activeLayer,
      setActiveLayer,
      availableLayerPrefixes,
      kestrelModel?.data.dataLayersConfig,
      currentTemperature,
      setCurrentTemperature,
      temperatureProbeLoading,
      setTemperatureProbeLoading,
      screenshotResult,
      currentUnit,
      dataValidationPanelOpen,
      textSegmentationPopoverVisible,
      orientationAxesPopoverVisible,
    ]
  )

  return (
    <KestrelViewerContext.Provider value={contextValue}>
      <KestrelInitializer
        pixelStreaming={pixelStreaming}
        videoParentRef={videoParentRef}
      />
      {children}
    </KestrelViewerContext.Provider>
  )
}
